import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Card } from '@components/common/Card';
import { Container } from '@components/common/Container';
import { Icon } from '@components/Icon';
import { isPublicSiteViewSearch, withPublicSiteView } from '@constants/publicSiteView';
import { useFamilyRegistrationFee } from '@hooks/useFamilyRegistrationFee';
import { useClassDeposit } from '@hooks/useClassDeposit';
import { useRegistrationVisibility } from '@hooks/useRegistrationVisibility';

const STEPS = [
  {
    number: 1,
    title: 'Review Courses Offered',
    icon: 'book' as const,
    description:
      'Browse our course descriptions, materials requirements, and pricing for Preschool, Elementary, Middle School, High School, and Adult programs.',
    linkLabel: 'View Courses',
    href: '/courses',
  },
  {
    number: 2,
    title: 'Register Your Family',
    icon: 'users' as const,
    description:
      'Complete family registration online before signing up for individual classes. This must be done once each academic year.',
    buttonLabel: 'Register Your Family',
  },
  {
    number: 3,
    title: 'Sign Up for Classes',
    icon: 'clipboard-list' as const,
    description:
      'Contact tutors directly for classes your students want to take. Get on class rosters or waiting lists early.',
  },
  {
    number: 4,
    title: 'Pay Deposit Fees',
    icon: 'brand-paypal' as const,
    description: '', // filled from site settings
  },
  {
    number: 5,
    title: 'Make Class Payments',
    icon: 'currency-dollar' as const,
    description:
      'Make tuition payments directly to tutors (Venmo, PayPal, or cash) according to their preferred payment method. Most tutors offer payment plans.',
  },
  {
    number: 6,
    title: 'Pay Family Registration Fee',
    icon: 'checkmark' as const,
    description: '', // filled from site settings
  },
];

const PRICING_NOTE =
  "Our tutors are highly qualified with education degrees or years of teaching experience. Registration fees cover group events, website fees, and supplies.";

export function HowToJoinSection() {
  const { showRegister } = useRegistrationVisibility();
  const [searchParams] = useSearchParams();
  const viewingPublicSite = isPublicSiteViewSearch(searchParams.toString());
  const { feeFormatted, feeHowToJoinDescription } = useFamilyRegistrationFee();
  const { depositFormatted, howToJoinDescription: depositHowToJoinDescription, hasDeposit } =
    useClassDeposit();
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [inViewSteps, setInViewSteps] = useState<Set<number>>(new Set());

  const steps = useMemo(
    () =>
      STEPS.map((step) => {
        if (step.number === 4) {
          return { ...step, description: depositHowToJoinDescription };
        }
        if (step.number === 6) {
          return { ...step, description: feeHowToJoinDescription };
        }
        return step;
      }),
    [depositHowToJoinDescription, feeHowToJoinDescription]
  );

  const pricingItems = useMemo(() => {
    const items = [
      {
        amount: feeFormatted,
        label: 'Family Registration Fee',
        note: 'yearly, cash only, first day',
      },
    ];
    if (hasDeposit) {
      items.push({
        amount: depositFormatted,
        label: 'Deposit per class',
        note: 'nonrefundable, secures spot',
      });
    }
    items.push({
      amount: 'Varies',
      label: 'Class tuition',
      note: 'set by tutors, based on length, materials, preparation',
    });
    return items;
  }, [feeFormatted, depositFormatted, hasDeposit]);

  useEffect(() => {
    // One observer for every step (instead of one per step).
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = stepRefs.current.indexOf(entry.target as (typeof stepRefs.current)[number]);
          if (index >= 0) setInViewSteps((prev) => new Set(prev).add(index + 1));
        });
      },
      { rootMargin: '0px 0px -15% 0px', threshold: 0.2 }
    );
    stepRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-to-join" className="bhg-landing-section bhg-landing-how-to-join">
      <Container size="lg">
        <div className="bhg-landing-how-to-join__header">
          <span className="bhg-landing-how-to-join__badge">6 Simple Steps</span>
          <h2 id="how-to-join-heading" className="bhg-landing-section__title">How to Join BHG</h2>
        </div>

        <div className="bhg-landing-how-to-join__grid">
          <div className="bhg-landing-how-to-join__steps">
            {steps.map((step, index) => (
              <div
                key={step.number}
                ref={(el) => {
                  stepRefs.current[index] = el;
                }}
                className={`bhg-landing-step-wrapper${inViewSteps.has(step.number) ? ' bhg-landing-step-wrapper--in-view' : ''}`}
              >
                <Card
                  elevation={2}
                  className="bhg-landing-step-card"
                >
                  <div className="bhg-landing-step-card__icon">
                    <Icon
                      type={step.icon}
                      style="filled"
                      colorVariant="blue"
                      size="md"
                      aria-label=""
                    />
                  </div>
                  <div className="bhg-landing-step-card__content">
                    <span className="bhg-landing-step-card__number">
                      Step {step.number}
                    </span>
                    <h3 className="bhg-landing-step-card__title">{step.title}</h3>
                    <p className="bhg-landing-step-card__description">
                      {step.description}
                    </p>
                    {step.href ? (
                      <Link
                        to={withPublicSiteView(step.href, viewingPublicSite)}
                        className="bhg-landing-step-card__link"
                      >
                        {step.linkLabel}
                      </Link>
                    ) : null}
                    {step.buttonLabel && showRegister ? (
                      <Link
                        to="/register"
                        className="bhg-button bhg-button--secondary bhg-button--sm"
                      >
                        {step.buttonLabel}
                      </Link>
                    ) : null}
                  </div>
                </Card>
                {index < steps.length - 1 && (
                  <div className="bhg-landing-step-connector" aria-hidden="true" />
                )}
              </div>
            ))}
          </div>

          <Card elevation={2} className="bhg-landing-pricing-card">
            <h3 className="bhg-landing-pricing-card__title">Tuition & Fees</h3>
            <ul className="bhg-landing-pricing-card__list">
              {pricingItems.map((item, i) => (
                <li key={i} className="bhg-landing-pricing-card__item">
                  <span className="bhg-landing-pricing-card__amount">
                    {item.amount}
                  </span>
                  <span className="bhg-landing-pricing-card__label">
                    {item.label}
                  </span>
                  <span className="bhg-landing-pricing-card__note">
                    {item.note}
                  </span>
                </li>
              ))}
            </ul>
            <p className="bhg-landing-pricing-card__footer">{PRICING_NOTE}</p>
          </Card>
        </div>
      </Container>
    </section>
  );
}
